import { useEffect, useRef } from "react";

/**
 * Real-time notifications socket.
 *
 * @param {string} token   JWT access token; if falsy, socket is not opened.
 * @param {(msg: object) => void} onMessage  Called for every message received.
 */
export default function useNotificationsSocket(token, onMessage) {
  const cbRef = useRef(onMessage);
  cbRef.current = onMessage;
  const wsRef = useRef(null);
  const attemptRef = useRef(0);
  const closedByUsRef = useRef(false);

  useEffect(() => {
    if (!token) return;
    closedByUsRef.current = false;

    const backend = process.env.REACT_APP_BACKEND_URL || "";
    // Turn https://host into wss://host, http://host into ws://host
    const wsBase = backend.replace(/^http/i, "ws");
    const url = `${wsBase}/api/ws/notifications?token=${encodeURIComponent(token)}`;

    let reconnectTimer = null;

    const connect = () => {
      try {
        const ws = new WebSocket(url);
        wsRef.current = ws;
        ws.onopen = () => {
          attemptRef.current = 0;
        };
        ws.onmessage = (evt) => {
          try {
            const msg = JSON.parse(evt.data);
            if (msg.type === "ping") return; // heartbeat — ignore
            cbRef.current && cbRef.current(msg);
          } catch (parseError) {
            console.debug("Ignoring non-JSON WS frame:", parseError?.message);
          }
        };
        ws.onclose = () => {
          wsRef.current = null;
          if (closedByUsRef.current) return;
          // Exponential backoff, capped at 30s
          attemptRef.current += 1;
          const delay = Math.min(30000, 1000 * Math.pow(2, attemptRef.current - 1));
          console.debug(`WS closed, reconnecting in ${delay}ms (attempt ${attemptRef.current})`);
          reconnectTimer = setTimeout(connect, delay);
        };
        ws.onerror = (event) => {
          console.debug("WS error, forcing close to trigger reconnect:", event?.type);
          try { ws.close(); } catch (closeError) {
            console.debug("WS close failed:", closeError?.message);
          }
        };
      } catch (connectError) {
        console.debug("WS constructor threw:", connectError?.message);
        attemptRef.current += 1;
        const delay = Math.min(30000, 1000 * Math.pow(2, attemptRef.current - 1));
        reconnectTimer = setTimeout(connect, delay);
      }
    };

    connect();

    return () => {
      closedByUsRef.current = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (wsRef.current) {
        try { wsRef.current.close(); } catch (closeError) {
          console.debug("WS cleanup close failed:", closeError?.message);
        }
        wsRef.current = null;
      }
    };
  }, [token]);
}
